import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
  type JSX
} from 'react';
import { ExternalLink } from '@gjensidige/builders-icons';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import type { TrackingProp } from '../types';
import { useTracking, getText } from '../utils';
import { ScreenReaderOnly } from '../';
import styles from './link.module.css';
import clsx from 'clsx';

const EXTERNAL_TEXTS = {
  no: 'Åpnes i nytt vindu',
  da: 'Åbner i nyt vindue',
  sv: 'Öppnas i nytt fönster',
  en: 'Opens in new window'
};

export type LinkProps<As extends React.ElementType = 'a'> =
  PolymorphicComponentPropWithRef<
    As,
    {
      icon?: 'auto' | boolean;
      tracking?: TrackingProp;
    }
  >;

const LinkComp = <As extends React.ElementType = 'a'>(
  {
    as,
    children,
    className,
    icon = 'auto',
    onClick,
    tracking = true,
    ...rest
  }: LinkProps<As>,
  ref: PolymorphicRef<As>
) => {
  const Tag = as || 'a';
  const innerRef = useRef<PolymorphicRef<As>>(null);
  const [lang, setLang] = useState<string | null>(null);
  const isExternal = rest.target === '_blank';
  const track = useTracking(tracking);
  const handleClick = (event: React.MouseEvent) => {
    track({
      eventCategory: 'link',
      eventAction: 'click',
      eventLabel: getText(event.currentTarget)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };

  useImperativeHandle(ref, () => innerRef.current);

  useEffect(() => {
    if (innerRef.current) {
      const closestLang = (innerRef.current as unknown as HTMLElement)
        ?.closest('[lang]')
        ?.getAttribute('lang');
      setLang(closestLang || 'en');
    }
  }, []);

  const text = EXTERNAL_TEXTS[lang as keyof typeof EXTERNAL_TEXTS];

  if (rest.target === '_blank')
    Object.assign(rest, { rel: 'noreferrer noopener' });

  return (
    <Tag
      className={clsx(styles.link, className)}
      onClick={handleClick}
      ref={innerRef}
      {...rest}
    >
      {children}
      {isExternal && icon && <ExternalLink className={styles.external} />}
      {isExternal && <ScreenReaderOnly>{text}</ScreenReaderOnly>}
    </Tag>
  );
};

export const Link = forwardRef<null>(LinkComp) as <
  As extends React.ElementType = 'a'
>(
  props: LinkProps<As>
) => JSX.Element;

(Link as React.FC).displayName = 'Link';
